/**
 * PMS-1123 — a supervisor PIN must never reach the offline queue.
 *
 * `Api.post` persists the whole payload to IndexedDB when `isOnline()` is false, and
 * `PendingRequestWorker.SyncData` writes failures back, so anything queued can sit on
 * disk indefinitely — across reloads, logouts and shift changes, on a terminal several
 * people share. Order data belongs there. A PIN does not: it is a credential that
 * authorises voids and sent-item deletions, and the whole point of the PMS-1123 work is
 * that it lives no longer than the request it authorised.
 *
 * A flaky reachability probe at the moment of submit is enough to trigger the queue, so
 * this is not a rare path. The guard refuses the request instead, and the caller shows
 * the failure — losing a void the user can retry, rather than leaving a PIN on disk.
 *
 * FOUR call sites send a PIN today: `WaiterPINDialog` (verify-pin), the void payload, the
 * bulk delete rows, and the waiter master-data save (`pos-waiter/save/v1`, which carries a
 * new PIN when one is typed — that one used to queue and now refuses). The rule is stated
 * over the PAYLOAD rather than over a list of URLs on purpose: it should not depend on
 * which screens happen to exist.
 */

/** The payload keys that carry a credential rather than data. */
const CREDENTIAL_KEYS = ["pin"];

function entryCarriesCredential(entry: unknown): boolean {
  if (entry === null || typeof entry !== "object") return false;
  const record = entry as Record<string, unknown>;
  return CREDENTIAL_KEYS.some((key) => {
    const value = record[key];
    return typeof value === "string" ? value.trim() !== "" : value != null;
  });
}

/**
 * True when this request body carries a PIN — on the body itself (the void request, and
 * verify-pin), on any entry of a bulk array (the sent-item delete), or on any row of a
 * `{ data: [...] }` import envelope.
 */
export function payloadCarriesPin(data: unknown): boolean {
  if (Array.isArray(data)) return data.some(entryCarriesCredential);
  if (entryCarriesCredential(data)) return true;
  const rows = (data as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(rows) ? rows.some(entryCarriesCredential) : false;
}
